import ReactECharts from 'echarts-for-react';

// Function to calculate PDF using Gaussian kernel
const calculatePDF = (data, minRange, maxRange, histogramBins) => {
    const binWidth = (maxRange - minRange) / histogramBins;
    const pdf = Array(histogramBins).fill(0);
    const bandwidth = binWidth; // Set the bandwidth for the Gaussian kernel

    data.forEach(x => {
        for (let i = 0; i < histogramBins; i++) {
            const binCenter = minRange + (i + 0.5) * binWidth;
            const kernel = Math.exp(-0.5 * Math.pow((x - binCenter) / bandwidth, 2)) / (bandwidth * Math.sqrt(2 * Math.PI));
            pdf[i] += kernel;
        }
    });

    // Normalize the PDF
    const totalArea = pdf.reduce((sum, val) => sum + val, 0) * binWidth; // Calculate area under the PDF
    return pdf.map(val => val / totalArea); // Normalize
};

const CostBetaFullChart = ({ design1Cost, design2Cost, design1title, design2title }) => {
    if (!design1Cost.length || !design2Cost.length) {
        return <div>Loading...</div>; // Or any other loading indicator
    }

    // Process the data
    const CostCumsum1 = design1Cost.map(arr => arr[arr.length - 1].reduce((acc, val) => acc + val, 0) / 1e6);
    const CostCumsum2 = design2Cost.map(arr => arr[arr.length - 1].reduce((acc, val) => acc + val, 0) / 1e6);

    const CI_design_1_over_2_MC = CostCumsum1.map((val, idx) => val / CostCumsum2[idx]);

    // Filter out NaN and Infinity values
    const filteredCI = CI_design_1_over_2_MC.filter(val => isFinite(val) && !isNaN(val));

    const Beta = 100 * filteredCI.filter(val => val < 1).length / filteredCI.length;
    const BetaLabel = `Distribution of Comparison Indicator (CI)`;

    // Create histogram data
    const histogramBins = 20;
    const minRange = 0;
    const maxRange = 2;
    const binWidth = (maxRange - minRange) / histogramBins;

    const histogramData = Array(histogramBins).fill(0);
    filteredCI.forEach(val => {
        if (val >= minRange && val <= maxRange) {
            const binIndex = Math.floor((val - minRange) / binWidth);
            histogramData[binIndex]++;
        }
    });

    // Normalize histogram data to convert counts to probability density
    const totalCount = filteredCI.length; // Use filtered count for normalization
    const normalizedHistogramData = histogramData.map(count => (count / totalCount) / binWidth); // Normalize by total count and bin width

    // Calculate PDF
    const pdfData = calculatePDF(filteredCI, minRange, maxRange, histogramBins);

    const option = {
        title: {
            text: BetaLabel,
            left: 'center',
        },
        xAxis: {
            type: 'value',
            min: minRange,
            max: maxRange,
            interval: 0.5,
            name: `Cost (${design1title}) / Cost (${design2title})`,
            nameLocation: 'middle',
            nameGap: 30,
            axisLabel: {
                formatter: (value) => `${value}`,
                fontSize: 14,
            },
            nameTextStyle: {
                fontSize: 14,
            },
            splitLine: {
                show: false,
            },
        },
        yAxis: {
            type: 'value',
            nameLocation: 'middle',
            nameGap: 30,
            name: 'Probability Density',
            axisLabel: {
                fontSize: 14,
                
            },
            nameTextStyle: {
                fontSize: 14,
            },
        },
        grid: {
            left: '10%',
            right: '10%',
            bottom: '15%',
        },
        series: [
            {
                name: 'Histogram',
                type: 'bar',
                data: normalizedHistogramData.map((value, index) => [index / 10, value]),
                barWidth: binWidth * 200,
                itemStyle: {
                    color: '#7B3294',
                },
            },
            {
                name: 'PDF',
                type: 'line',
                data: pdfData.map((value, index) => [index / 10, value]),
                itemStyle: {
                    color: '#000',
                },
                smooth: true, // Makes the line smooth
            },
            {
                name: 'Shaded Region 1',
                type: 'line',
                markArea: {
                    data: [
                        [
                            {
                                name: `${design1title} Preferable`,
                                xAxis: minRange,
                                itemStyle: {
                                    color: 'rgba(0, 114, 188, 0.1)',
                                },
                                label: {
                                    position: 'insideTop',
                                    formatter: `${design1title} Preferable\nβ=${Beta.toFixed(1)}%`,
                                    color: '#0072B2',
                                    fontSize: 14,
                                },
                            },
                            {
                                xAxis: 1,
                            },
                        ],
                    ],
                },
            },
            {
                name: 'Shaded Region 2',
                type: 'line',
                markArea: {
                    data: [
                        [
                            {
                                name: `${design2title} Preferable`,
                                xAxis: 1,
                                itemStyle: {
                                    color: 'rgba(217, 83, 25, 0.1)',
                                },
                                label: {
                                    position: 'insideTop',
                                    formatter: `${design2title} Preferable\nβ=${(100 - Beta).toFixed(1)}%`,
                                    color: '#D95319',
                                    fontSize: 14,
                                },
                            },
                            {
                                xAxis: maxRange,
                            },
                        ],
                    ],
                },
            },
            {
                name: 'Vertical Line at 1',
                type: 'line',
                markLine: {
                    data: [
                        {
                            xAxis: 1,
                            lineStyle: {
                                type: 'dashed',
                                color: '#000000',
                            },
                        },
                    ],
                },
            },
        ],
    };

    return (
        <div>

            <ReactECharts option={option} style={{ height: '500px', width: '100%' }} />
        </div>
    );
};

export default CostBetaFullChart;
